import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Accordion, AccordionDetails, AccordionSummary, Button, Typography } from '@mui/material';
import { useNavigate, useResolvedPath } from 'react-router-dom';
import { useConfirm } from 'material-ui-confirm';
import ButtonWrapper from './styles/ButtonWrapper';
import MainSection from './components/MainSection/MainSection';
// eslint-disable-next-line boundaries/element-types
import ScheduleWidget from '../../../../../schedule/ScheduleWidget/ScheduleWidget';
import UserSection from './components/UserSection/UserSection';
import {
  useManualStartReportTaskMutation,
  usePatchReportTaskMutation,
  usePostReportTaskMutation,
} from '../../../../../../entities/reports/reportsResource/redux/reportsResource.api';
import { prepareControls, prepareRecipients, validateReportTaskForm } from '../../utils';
import AlertLabel from './components/AlertLabel/AlertLabel';

function ScheduleReport({ companyId, controls, setControls, selectedType, setSelectedType, reportTask, reportName }) {
  const navigate = useNavigate();
  const base = useResolvedPath('..');

  const confirm = useConfirm();

  const [taskName, setTaskName] = useState(reportName);
  const [schedule, setSchedule] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [startDate, setStartDate] = useState(new Date());

  const [isSuccessSave, setIsSuccessSave] = useState(null);
  const [isSuccessStart, setIsSuccessStart] = useState(null);
  const isFormSent = isSuccessSave !== null;
  const isTaskTryStart = isSuccessStart !== null;

  const [createReportTask] = usePostReportTaskMutation();
  const [updateReportTask] = usePatchReportTaskMutation();
  const [manualStartReportTask] = useManualStartReportTaskMutation();

  const isFormValid = useMemo(() => {
    return validateReportTaskForm({ selectedType, taskName, schedule, controls, recipients, startDate });
  }, [selectedType, taskName, schedule, controls, recipients, startDate]);

  const initialUserIds = useMemo(
    () => reportTask?.reportTaskRecipients?.map((r) => r.appUserId) || [],
    [reportTask?.reportTaskRecipients],
  );

  useEffect(() => {
    if (!reportTask) return;

    setTaskName(reportTask.taskName);
    setSchedule(reportTask.schedule);
    setStartDate(reportTask?.nextRunDate ? new Date(reportTask.nextRunDate) : new Date());
  }, [reportTask]);

  const handleClickSave = async () => {
    const changeableBody = {
      taskName,
      schedule,
      reportFormat: selectedType,
      nextRunDate: startDate?.toISOString(),
      taskParams: prepareControls(controls),
      reportTaskRecipients: prepareRecipients(recipients),
    };

    if (reportTask?.reportTaskId) {
      const response = await updateReportTask({
        id: reportTask?.reportTaskId,
        body: { ...changeableBody, reportTaskId: reportTask?.reportTaskId },
      });
      setIsSuccessSave(!response?.error);
      setIsSuccessStart(null);

      if (!response?.error) setTimeout(() => navigate(`${base.pathname}/report-tasks`), 1200);
      return;
    }

    const response = await createReportTask({ body: { ...changeableBody, companyId, status: 'PENDING' } });
    setIsSuccessSave(!response?.error);

    if (!response?.error) setTimeout(() => navigate(`${base.pathname}/report-tasks`), 1200);
  };

  const manualStart = async () => {
    if (!reportTask?.reportTaskId) return;

    const response = await manualStartReportTask(reportTask?.reportTaskId);
    setIsSuccessStart(!response?.error);
    setIsSuccessSave(null);
    setTimeout(() => setIsSuccessStart(null), 3000);
  };

  const handleClickManualStart = () => {
    confirm({
      title: 'Ручной запуск отчета',
      confirmationText: 'Да',
      cancellationText: 'Отмена',
      description: `Вы действительно хотите запустить отчет по расписанию?`,
    })
      .then(() => manualStart())
      .catch(() => {});
  };

  return (
    <>
      <Typography variant="h6" sx={{ color: 'text.secondary', m: '1rem 0' }}>
        {`${reportTask?.reportTaskId ? 'Редактирование' : 'Запуск'} отчета по расписанию`}
      </Typography>
      <Accordion defaultExpanded>
        <AccordionSummary>
          <Typography>Основное</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <MainSection
            companyId={companyId}
            controls={controls}
            setControls={setControls}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            reportName={taskName}
            setReportName={setTaskName}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary>
          <Typography>Расписание</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ScheduleWidget
            onChangeSсhedule={(value) => setSchedule(value)}
            scheduleValue={reportTask?.schedule || ' '}
            startDate={startDate}
            onChangeStartDate={(value) => setStartDate(value)}
          />
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary>
          <Typography>Список пользователей для рассылки</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <UserSection
            companyId={companyId}
            users={recipients}
            setUsers={setRecipients}
            initialUserIds={initialUserIds}
          />
        </AccordionDetails>
      </Accordion>
      <AlertLabel isShow={isFormSent} isCorrect={isSuccessSave} />
      <AlertLabel
        isShow={isTaskTryStart}
        isCorrect={isSuccessStart}
        successText="Отчет разослан"
        errorText="Произошла ошибка при рассылке отчета"
      />
      <ButtonWrapper>
        <Button variant="contained" onClick={() => navigate(-1)}>
          Отменить
        </Button>
        {!!reportTask && (
          <Button disabled={!isFormValid} variant="contained" onClick={handleClickManualStart}>
            Ручной запуск
          </Button>
        )}
        <Button disabled={!isFormValid} variant="contained" onClick={handleClickSave}>
          Сохранить
        </Button>
      </ButtonWrapper>
    </>
  );
}

ScheduleReport.propTypes = {
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  controls: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  setControls: PropTypes.func.isRequired,
  selectedType: PropTypes.string.isRequired,
  setSelectedType: PropTypes.func.isRequired,
  reportTask: PropTypes.shape().isRequired,
  reportName: PropTypes.string,
};

ScheduleReport.defaultProps = {
  reportName: '',
};

export default ScheduleReport;
